import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Car, Clock, DollarSign, Activity, Users,
  RefreshCw, Calendar, ChevronDown, Phone, Star,
  MapPin, Navigation, Circle, Trophy, TrendingUp,
  User, ArrowRight, Plus, HandCoins
} from 'lucide-react';
import { 
  AreaChart, Area, BarChart, Bar, ComposedChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import useWebSocket from '../../hooks/useWebSocket';
import { getDrivers, getRides } from '../../services/api';
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_OPTIONS, getCarMarkerOptions } from '../../config/googleMaps';
import CreateOrderModal from './CreateOrderModal';
import NewRideNotification from './NewRideNotification';
import { getRideDestination, getRidePickup, getRideScheduledAt } from '../../utils/rideFormat';
import { useSettings } from '../../context/SettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ALBANIA_CENTER = { lat: 41.3275, lng: 19.8187 };

// Format currencies - Standard format with decimals
const formatLEK = (amount) => {
  if (!amount && amount !== 0) return '0.00 L';
  return new Intl.NumberFormat('en-US', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  }).format(amount) + ' L';
};

const formatALL = formatLEK; // Alias for backward compatibility

const formatEUR = (amount) => {
  if (!amount && amount !== 0) return '€0.00';
  return '€' + new Intl.NumberFormat('en-US', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  }).format(amount);
};

// Exchange rate: 1 EUR = 100 LEK
const EUR_TO_LEK = 100;

const mapLegacyWebsiteBooking = (msg) => ({
  id: msg.booking_id,
  ticket_number: msg.booking_id ? `WEB-${String(msg.booking_id).slice(0, 6).toUpperCase()}` : 'WEB',
  passenger_name: msg.customer_name || 'Pasagjer',
  passenger_phone: msg.customer_phone || '',
  passenger_email: msg.customer_email || '',
  pickup: { address: msg.pickup_address || 'Pikë nisje' },
  destination: { address: msg.destination_address || 'Destinacion' },
  price_lek: msg.price_lek ?? msg.estimated_price ?? null,
  price_eur: msg.price_eur ?? null,
  estimated_price: msg.estimated_price ?? msg.price_lek ?? null,
  price_determined: msg.price_determined,
  price_source: msg.price_source,
  status: 'pending',
  source: 'website',
});

// Live Clock Component
const LiveClock = ({ isConnected }) => {
  const [time, setTime] = useState(new Date());
  
  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const hours = String(time.getHours()).padStart(2, '0');
  const minutes = String(time.getMinutes()).padStart(2, '0');
  const seconds = String(time.getSeconds()).padStart(2, '0');

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${isConnected ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
      <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`}></span>
      <span>Live</span>
      <span className="text-gray-500 font-mono">{hours}:{minutes}:{seconds}</span>
    </div>
  );
};

// Soft KPI Card with navigation
const KPICard = ({ title, value, subtitle, icon: Icon, color, bgColor, onClick }) => (
  <div 
    onClick={onClick}
    className="bg-white rounded-xl p-4 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all cursor-pointer group"
  >
    <div className="flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl ${bgColor} flex items-center justify-center group-hover:scale-105 transition-transform`}>
        <Icon className={`w-5 h-5 ${color}`} strokeWidth={1.5} />
      </div>
      <div className="flex-1">
        <p className="text-xl font-bold text-gray-800">{value}</p>
        <p className="text-sm text-gray-500">{title}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  </div>
);

// Revenue Card with navigation
const RevenueCard = ({ amountAll, amountEur, onClick }) => (
  <div 
    onClick={onClick}
    className="bg-white rounded-xl p-4 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all cursor-pointer group"
  >
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center group-hover:scale-105 transition-transform">
        <DollarSign className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
      </div>
      <div className="flex-1">
        <p className="text-xl font-bold text-gray-800">{formatALL(amountAll)}</p>
        {amountEur > 0 && <p className="text-sm text-blue-500 font-medium">{formatEUR(amountEur)}</p>}
        <p className="text-sm text-gray-500">Të Ardhura</p>
      </div>
    </div>
  </div>
);

// Period Selector
const PeriodSelector = ({ value, onChange, customRange, onCustomRangeChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [startDate, setStartDate] = useState(customRange?.start || '');
  const [endDate, setEndDate] = useState(customRange?.end || '');
  
  const periods = [
    { value: 'today', label: 'Sot' },
    { value: 'week', label: '7 Ditë' },
    { value: 'month', label: '30 Ditë' },
    { value: 'custom', label: 'Custom' },
  ];

  const handleCustomApply = () => {
    if (startDate && endDate) {
      onCustomRangeChange({ start: startDate, end: endDate });
      onChange('custom');
      setShowCustom(false);
      setIsOpen(false);
    }
  };
  
  return (
    <div className="relative">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-all">
        <Calendar className="w-4 h-4" strokeWidth={1.5} />
        <span>{periods.find(p => p.value === value)?.label}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => { setIsOpen(false); setShowCustom(false); }} />
          <div className="absolute top-full mt-1 right-0 bg-white rounded-lg shadow-lg z-20 py-1 min-w-[140px] border border-gray-100">
            {!showCustom ? periods.map(p => (
              <button key={p.value} onClick={() => { if (p.value === 'custom') setShowCustom(true); else { onChange(p.value); setIsOpen(false); }}}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${value === p.value ? 'bg-amber-50 text-amber-600' : 'text-gray-600'}`}>
                {p.label}
              </button>
            )) : (
              <div className="p-3 space-y-2">
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded" />
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded" />
                <div className="flex gap-2">
                  <button onClick={() => setShowCustom(false)} className="flex-1 px-2 py-1 text-xs border rounded text-gray-500">Anulo</button>
                  <button onClick={handleCustomApply} className="flex-1 px-2 py-1 text-xs bg-amber-500 text-white rounded">Apliko</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Driver Card with trip visualization
const DriverCard = ({ driver, onClick }) => {
  const statusConfig = {
    online: { label: 'I lirë', color: 'bg-emerald-500', dotColor: 'bg-emerald-400' },
    busy: { label: 'Në udhëtim', color: 'bg-amber-500', dotColor: 'bg-amber-400' },
    break: { label: 'Pushim', color: 'bg-orange-500', dotColor: 'bg-orange-400' },
    offline: { label: 'Offline', color: 'bg-gray-400', dotColor: 'bg-gray-300' }
  };
  const status = statusConfig[driver.status] || statusConfig.offline;
  const isOnTrip = driver.status === 'busy' && driver.current_ride;
  
  return (
    <div onClick={onClick} className="p-3 bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all cursor-pointer">
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
          {driver.photo ? (
            <img src={driver.photo} alt={driver.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-sm font-medium text-gray-500">{driver.name?.charAt(0) || 'D'}</span>
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-gray-800 truncate">{driver.name}</p>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-white ${status.color}`}>
              {status.label}
            </span>
          </div>
          
          {/* Rating & Phone */}
          <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center gap-0.5">
              <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
              <span className="text-xs text-gray-500">{driver.rating?.toFixed(1) || '5.0'}</span>
            </div>
            <span className="text-gray-300">•</span>
            <span className="text-xs text-gray-500">{driver.phone || 'N/A'}</span>
          </div>
          
          {/* Trip Visualization for busy drivers */}
          {isOnTrip && (
            <div className="mt-2 p-2 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2">
                {/* Pickup */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <Circle className="w-2.5 h-2.5 text-emerald-500 fill-emerald-500 flex-shrink-0" />
                    <span className="text-[10px] text-gray-600 truncate">
                      {driver.current_ride?.pickup?.address || 'Pikë nisje'}
                    </span>
                  </div>
                </div>
                
                {/* Animated Line with Car */}
                <div className="relative w-12 h-4 flex items-center">
                  <div className="absolute inset-x-0 top-1/2 h-px bg-gray-300 -translate-y-1/2"></div>
                  <div className="absolute animate-car-move">
                    <Car className="w-3.5 h-3.5 text-amber-600" />
                  </div>
                </div>
                
                {/* Destination */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 justify-end">
                    <span className="text-[10px] text-gray-600 truncate">
                      {driver.current_ride?.dropoff?.address || 'Destinacion'}
                    </span>
                    <Navigation className="w-2.5 h-2.5 text-red-500 flex-shrink-0" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Map Stats Overlay - Top Left Position with opacity
const MapStatsOverlay = ({ drivers, pendingRides }) => {
  const online = drivers.filter(d => d.status === 'online').length;
  const busy = drivers.filter(d => d.status === 'busy').length;
  
  return (
    <div className="absolute top-3 left-3 z-10">
      <div className="bg-white/70 backdrop-blur-md rounded-xl px-4 py-2.5 shadow-lg border border-white/30">
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
            <span className="font-bold text-emerald-700">{online}</span>
            <span className="text-gray-600">online</span>
          </div>
          <div className="w-px h-4 bg-gray-300/50"></div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
            <span className="font-bold text-amber-700">{busy}</span>
            <span className="text-gray-600">busy</span>
          </div>
          <div className="w-px h-4 bg-gray-300/50"></div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse"></span>
            <span className="font-bold text-rose-700">{pendingRides}</span>
            <span className="text-gray-600">porosi në pritje</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Live Orders Panel - With Tabs for Live and Scheduled - REDESIGNED COMPACT VERSION
const LiveOrdersPanel = ({ rides, onRideClick }) => {
  const [activeTab, setActiveTab] = useState('live');
  const { formatDateTime } = useSettings();
  
  const liveRides = rides.filter(r => ['pending', 'accepted', 'arriving', 'arrived_pickup', 'in_progress'].includes(r.status));
  const scheduledRides = rides.filter(
    (r) => r.status === 'scheduled' || getRideScheduledAt(r)
  );
  
  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Pritje', bg: 'bg-rose-500', text: 'text-white', dot: 'animate-pulse' },
      accepted: { label: 'Pranuar', bg: 'bg-blue-500', text: 'text-white', dot: '' },
      arriving: { label: 'Vjen', bg: 'bg-violet-500', text: 'text-white', dot: '' },
      arrived_pickup: { label: 'Arritur', bg: 'bg-cyan-500', text: 'text-white', dot: '' },
      in_progress: { label: 'Udhëtim', bg: 'bg-amber-500', text: 'text-white', dot: '' },
      scheduled: { label: 'Rezerv.', bg: 'bg-indigo-500', text: 'text-white', dot: '' }
    };
    return configs[status] || configs.pending;
  };

  const formatScheduledTime = (timeStr) => {
    if (!timeStr) return '';
    const date = new Date(timeStr);
    return `${date.toLocaleDateString('sq-AL', { day: '2-digit', month: 'short' })} ${date.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const currentRides = activeTab === 'live' ? liveRides : scheduledRides;

  return (
    <div className="flex flex-col h-full" data-testid="live-orders-panel">
      {/* Header with Tabs */}
      <div className="flex items-center border-b border-gray-100 bg-gray-50/50">
        <button
          onClick={() => setActiveTab('live')}
          data-testid="live-tab"
          className={`flex-1 px-3 py-2.5 text-sm font-bold transition-colors ${
            activeTab === 'live' 
              ? 'text-rose-600 bg-white border-b-2 border-rose-500' 
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <div className={`w-2 h-2 rounded-full ${activeTab === 'live' ? 'bg-rose-500 animate-pulse' : 'bg-gray-300'}`} />
            <span>Live</span>
            <span className={`min-w-[20px] px-1.5 py-0.5 rounded-full text-xs font-bold ${
              activeTab === 'live' ? 'bg-rose-500 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {liveRides.length}
            </span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('scheduled')}
          data-testid="scheduled-tab"
          className={`flex-1 px-3 py-2.5 text-sm font-bold transition-colors ${
            activeTab === 'scheduled' 
              ? 'text-indigo-600 bg-white border-b-2 border-indigo-500' 
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Calendar className="w-3.5 h-3.5" />
            <span>Rezervime</span>
            <span className={`min-w-[20px] px-1.5 py-0.5 rounded-full text-xs font-bold ${
              activeTab === 'scheduled' ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {scheduledRides.length}
            </span>
          </div>
        </button>
      </div>

      {/* Compact Orders List - Hidden Scrollbar */}
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {activeTab === 'scheduled' && currentRides.length > 0 && (
          <div className="sticky top-0 z-10 grid grid-cols-[44px_minmax(72px,1fr)_76px_minmax(0,1fr)_76px] gap-1 px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-[9px] font-bold text-gray-400 uppercase leading-tight">
            <span />
            <span>Klienti</span>
            <span className="text-right">Data/Ora e rezervimit</span>
            <span>Rruga / Shoferi</span>
            <span className="text-right">Data/Ora e udhetimit</span>
          </div>
        )}
        {currentRides.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {currentRides.map((ride, idx) => {
              const scheduledAt = getRideScheduledAt(ride);
              const config = getStatusConfig(ride.status);
              return (
                <div 
                  key={ride.id || idx} 
                  onClick={() => onRideClick?.(ride)}
                  data-testid={`order-item-${ride.id || idx}`}
                  className="px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors group"
                >
                  {activeTab === 'scheduled' ? (
                    <div className="grid grid-cols-[44px_minmax(72px,1fr)_76px_minmax(0,1fr)_76px] gap-1 items-start">
                      <span className={`px-1 py-0.5 rounded text-[10px] font-bold text-center ${config.bg} ${config.text}`}>
                        {config.label}
                      </span>
                      <div className="min-w-0">
                        <span className="text-xs font-medium text-gray-800 truncate block">
                          {ride.passenger_name || '—'}
                        </span>
                        {ride.passenger_phone && (
                          <span className="text-[10px] text-gray-500 block truncate">{ride.passenger_phone}</span>
                        )}
                      </div>
                      <span
                        className="text-[10px] text-gray-500 text-right leading-tight pt-0.5"
                        title="Data/Ora e rezervimit"
                      >
                        {ride.created_at ? formatScheduledTime(ride.created_at) : '—'}
                      </span>
                      <div className="min-w-0 pt-0.5 space-y-0.5">
                        <div className="flex items-center gap-1 min-w-0">
                          <Circle className="w-2 h-2 text-emerald-500 fill-emerald-500 flex-shrink-0" />
                          <span className="text-[10px] font-medium text-gray-700 truncate">
                            {getRidePickup(ride).split(',')[0]}
                          </span>
                          <ArrowRight className="w-2 h-2 text-gray-300 flex-shrink-0" />
                          <span className="text-[10px] font-medium text-gray-700 truncate">
                            {getRideDestination(ride).split(',')[0]}
                          </span>
                        </div>
                        {ride.driver_name && (
                          <div className="flex items-center gap-1 text-[10px] text-gray-500 truncate">
                            <Car className="w-2.5 h-2.5 flex-shrink-0" />
                            {ride.driver_name}
                          </div>
                        )}
                      </div>
                      <span className="text-[10px] text-indigo-600 font-semibold text-right leading-tight pt-0.5">
                        {scheduledAt ? formatScheduledTime(scheduledAt) : '—'}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className={`flex-shrink-0 px-2 py-1 rounded text-[11px] font-bold ${config.bg} ${config.text} ${config.dot}`}>
                        {config.label}
                      </span>
                      <div className="flex-1 flex items-center gap-1.5 min-w-0">
                        <Circle className="w-2.5 h-2.5 text-emerald-500 fill-emerald-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-700 truncate max-w-[80px]">
                          {getRidePickup(ride).split(',')[0]}
                        </span>
                        <ArrowRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
                        <Navigation className="w-2.5 h-2.5 text-rose-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-700 truncate max-w-[80px]">
                          {getRideDestination(ride).split(',')[0]}
                        </span>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        {ride.driver_name ? (
                          <span className="text-xs text-gray-500 font-medium">{ride.driver_name.split(' ')[0]}</span>
                        ) : (
                          <span className="text-xs text-gray-400">#{ride.ride_number?.slice(-4) || idx + 1}</span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {activeTab !== 'scheduled' && ride.passenger_name && (
                    <div className="flex items-center gap-2 mt-1 ml-[52px]">
                      <User className="w-3 h-3 text-gray-400" />
                      <span className="text-sm text-gray-600">{ride.passenger_name}</span>
                      {ride.passenger_phone && (
                        <>
                          <span className="text-gray-300">•</span>
                          <Phone className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">{ride.passenger_phone}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400">
            <div className="text-center py-8">
              {activeTab === 'live' ? (
                <>
                  <Car className="w-10 h-10 mx-auto mb-2 opacity-30" strokeWidth={1.5} />
                  <p className="text-sm font-medium">Nuk ka porosi aktive</p>
                  <p className="text-xs mt-1 text-gray-300">Porosite e reja do shfaqen këtu</p>
                </>
              ) : (
                <>
                  <Calendar className="w-10 h-10 mx-auto mb-2 opacity-30" strokeWidth={1.5} />
                  <p className="text-sm font-medium">Nuk ka rezervime</p>
                  <p className="text-xs mt-1 text-gray-300">Rezervimet e ardhshme do shfaqen këtu</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Top Drivers Component
const TopDriversCard = ({ drivers, period, onClick }) => {
  // Sort drivers by completed rides or rating
  const topDrivers = [...drivers]
    .filter(d => d.total_rides > 0 || d.rating > 0)
    .sort((a, b) => (b.total_rides || 0) - (a.total_rides || 0))
    .slice(0, 5);

  const getMedalColor = (index) => {
    const colors = ['text-yellow-500', 'text-gray-400', 'text-amber-600', 'text-gray-500', 'text-gray-500'];
    return colors[index] || colors[4];
  };

  return (
    <div className="bg-white rounded-xl p-4 border border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500" />
          <h3 className="text-sm font-semibold text-gray-700">Shoferët më të mirë</h3>
        </div>
        <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">
          {period === 'today' ? 'Sot' : period === 'week' ? '7 ditë' : '30 ditë'}
        </span>
      </div>
      <div className="space-y-2">
        {topDrivers.length > 0 ? topDrivers.map((driver, idx) => (
          <div 
            key={driver.id || idx}
            onClick={() => onClick?.(driver)}
            className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <div className="flex items-center justify-center w-6 h-6">
              <span className={`text-sm font-bold ${getMedalColor(idx)}`}>#{idx + 1}</span>
            </div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              {driver.photo ? (
                <img src={driver.photo} alt={driver.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs font-bold text-amber-600">{driver.name?.charAt(0) || 'D'}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-800 truncate">{driver.name}</p>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-0.5">
                  <Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />
                  <span className="text-[10px] text-gray-500">{driver.rating?.toFixed(1) || '5.0'}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs font-bold text-gray-800">{driver.total_rides || 0}</p>
              <p className="text-[10px] text-gray-400">udhëtime</p>
            </div>
          </div>
        )) : (
          <div className="text-center py-4 text-gray-400">
            <TrendingUp className="w-6 h-6 mx-auto mb-1 opacity-30" />
            <p className="text-xs">Nuk ka të dhëna</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Map Fallback Component
const MapFallback = ({ drivers, pendingRides }) => {
  const online = drivers.filter(d => d.status === 'online').length;
  const busy = drivers.filter(d => d.status === 'busy').length;
  
  return (
    <div className="relative h-full bg-gradient-to-br from-blue-50 via-slate-50 to-emerald-50 flex items-center justify-center">
      {/* Albania Map Silhouette */}
      <div className="absolute inset-0 flex items-center justify-center opacity-20">
        <svg viewBox="0 0 200 200" className="w-48 h-48">
          <path d="M100,20 L130,40 L140,70 L150,90 L145,120 L130,150 L100,180 L70,160 L55,130 L50,100 L60,60 L80,35 Z" 
            fill="none" stroke="#64748b" strokeWidth="2"/>
          {/* Animated dots representing drivers */}
          {online > 0 && <circle cx="100" cy="80" r="5" className="fill-emerald-500 animate-pulse"/>}
          {online > 1 && <circle cx="85" cy="110" r="4" className="fill-emerald-500 animate-pulse" style={{animationDelay: '0.3s'}}/>}
          {busy > 0 && <circle cx="115" cy="100" r="5" className="fill-amber-500 animate-pulse" style={{animationDelay: '0.5s'}}/>}
          {online > 2 && <circle cx="95" cy="140" r="4" className="fill-emerald-500 animate-pulse" style={{animationDelay: '0.7s'}}/>}
        </svg>
      </div>
      
      {/* Center Content */}
      <div className="relative text-center z-10">
        <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-white shadow-lg flex items-center justify-center border border-gray-100">
          <MapPin className="w-8 h-8 text-amber-500" strokeWidth={1.5} />
        </div>
        <p className="text-sm font-semibold text-gray-700">Harta Live</p>
        <p className="text-xs text-gray-400 mt-1">Aktive në app.tiataxi.al</p>
      </div>
      
      <MapStatsOverlay drivers={drivers} pendingRides={pendingRides} />
    </div>
  );
};

// Live Map Component  
const LiveDriversMap = ({ drivers, onDriverClick, pendingRides }) => {
  const [mapError, setMapError] = useState(false);
  
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  const mapContainerStyle = { width: '100%', height: '100%', minHeight: '350px' };
  
  // Car icon for markers
  const getCarIcon = (status) => {
    const colors = { 
      online: '#22c55e', 
      available: '#22c55e',
      busy: '#f59e0b', 
      in_ride: '#f59e0b',
      break: '#f97316', 
      offline: '#9ca3af' 
    };
    return {
      path: 'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z',
      fillColor: colors[status] || colors.offline,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 1.5,
      scale: 1.4,
      anchor: new window.google.maps.Point(12, 12),
      rotation: 0
    };
  };

  // Handle map load errors
  const handleMapError = useCallback(() => {
    setMapError(true);
  }, []);

  // Show fallback if no API key, load error, or map error
  if (!GOOGLE_MAPS_API_KEY || loadError || mapError) {
    return <MapFallback drivers={drivers} pendingRides={pendingRides} />;
  }
  
  if (!isLoaded) {
    return (
      <div className="relative h-full bg-slate-50 flex items-center justify-center rounded-lg">
        <RefreshCw className="w-5 h-5 text-slate-300 animate-spin" />
        <MapStatsOverlay drivers={drivers} pendingRides={pendingRides} />
      </div>
    );
  }

  const driversWithPosition = drivers.filter(d => d.position?.lat && d.position?.lng);

  return (
    <div className="relative h-full">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={ALBANIA_CENTER}
        zoom={8}
        onLoad={() => {
          // Check if there's an authentication error after 2 seconds
          setTimeout(() => {
            const errorDiv = document.querySelector('.gm-err-container');
            if (errorDiv) {
              handleMapError();
            }
          }, 2000);
        }}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: 'greedy',
          scrollwheel: true,
          isFractionalZoomEnabled: true,
          styles: [
            { featureType: 'poi', stylers: [{ visibility: 'off' }] },
            { featureType: 'transit', stylers: [{ visibility: 'off' }] }
          ]
        }}
      >
        {driversWithPosition.map(driver => (
          <Marker
            key={driver.id}
            position={{ lat: driver.position.lat, lng: driver.position.lng }}
            icon={getCarIcon(driver.status)}
            onClick={() => onDriverClick?.(driver)}
          />
        ))}
      </GoogleMap>
      <MapStatsOverlay drivers={drivers} pendingRides={pendingRides} />
    </div>
  );
};

// Charts
const RevenueChart = ({ data }) => {
  const label = data?.[0]?.date?.includes(':') ? 'Orë' : 'Ditë';
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Të Ardhurat ({label})</h3>
        <div className="flex gap-3 text-[10px] text-gray-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400"></span>LEK</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400"></span>EUR</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={150}>
        <ComposedChart data={data || []}>
          <defs>
            <linearGradient id="gradAll" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#34d399" stopOpacity={0.15}/>
              <stop offset="100%" stopColor="#34d399" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
          <YAxis yAxisId="all" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={30} />
          <YAxis yAxisId="eur" orientation="right" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={30} />
          <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '10px' }} />
          <Area yAxisId="all" type="monotone" dataKey="revenue_all" stroke="#34d399" strokeWidth={1.5} fill="url(#gradAll)" name="LEK" />
          <Line yAxisId="eur" type="monotone" dataKey="revenue_eur" stroke="#60a5fa" strokeWidth={1.5} dot={{ r: 2 }} name="EUR" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

const RidesChart = ({ data }) => {
  const label = data?.[0]?.date?.includes(':') ? 'Orë' : 'Ditë';
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-100">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Udhëtimet ({label})</h3>
      <ResponsiveContainer width="100%" height={150}>
        <BarChart data={data || []}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={20} />
          <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '10px' }} />
          <Bar dataKey="rides" fill="#fbbf24" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Main Dashboard
export const AdminDashboardV2 = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [newOrderPopup, setNewOrderPopup] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const previousRidesRef = useRef([]);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {
        // Fallback to beep
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.3;
        oscillator.start();
        setTimeout(() => { oscillator.stop(); ctx.close(); }, 200);
      });
    } catch (e) {}
  }, []);

  // Show browser notification
  const showBrowserNotification = useCallback((order) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification('🚕 Porosi e Re!', {
        body: `${order.passenger_name || 'Pasagjer'} - ${getRidePickup(order)}`,
        icon: '/logo192.png',
        tag: 'new-order',
        requireInteraction: true
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/admin/dashboard?period=${period}`;
      if (period === 'custom' && customRange.start && customRange.end) {
        url += `&start_date=${customRange.start}&end_date=${customRange.end}`;
      }
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [period, customRange]);

  const fetchDrivers = useCallback(async () => {
    try {
      const driversData = await getDrivers();
      setDrivers(driversData || []);
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  const fetchRides = useCallback(async () => {
    try {
      const ridesData = await getRides();
      
      // Check for new pending rides to show popup
      if (previousRidesRef.current.length > 0) {
        const previousIds = previousRidesRef.current.map(r => r.id);
        const newPendingRides = (ridesData || []).filter(
          r => r.status === 'pending' && !previousIds.includes(r.id)
        );
        if (newPendingRides.length > 0) {
          const newOrder = newPendingRides[0];
          setNewOrderPopup(newOrder);
          playNotificationSound();
          showBrowserNotification(newOrder);
        }
      }
      
      previousRidesRef.current = ridesData || [];
      setRides(ridesData || []);
    } catch (error) {
      console.error('Error fetching rides:', error);
    }
  }, [playNotificationSound, showBrowserNotification]);

  // Fetch unsettled cash totals
  const [unsettledCash, setUnsettledCash] = useState({ grand_total_all: 0, drivers_count: 0 });
  
  const fetchUnsettledCash = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/cash/unsettled-totals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setUnsettledCash(result);
      }
    } catch (error) {
      console.error('Error fetching unsettled cash:', error);
    }
  }, []);

  const { isConnected } = useWebSocket('dispatcher', (msg) => {
    if (msg.type === 'new_ride' && msg.data) {
      // Show popup for new ride from WebSocket
      setNewOrderPopup(msg.data);
      playNotificationSound();
      showBrowserNotification(msg.data);
    }
    if (msg.type === 'new_website_booking') {
      // Backend sends full ride via `new_ride` first; only refresh data here.
    }
    if (
      ['new_ride', 'new_website_booking', 'ride_updated', 'driver_status_changed', 'driver_location_updated', 'driver.location.updated', 'driver_moved', 'driver_location'].includes(msg.type)
    ) {
      fetchDashboard();
      fetchDrivers();
      fetchRides();
    }
  });

  useEffect(() => { 
    fetchDashboard(); 
    fetchDrivers();
    fetchRides();
    fetchUnsettledCash();
  }, [fetchDashboard, fetchDrivers, fetchRides, fetchUnsettledCash]);
  
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboard();
      fetchDrivers();
      fetchRides();
      fetchUnsettledCash();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard, fetchDrivers, fetchRides, fetchUnsettledCash]);

  const kpi = data?.kpi || {};
  const charts = data?.charts || {};
  const onlineDrivers = drivers.filter(d => d.status && d.status !== 'offline');

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <RefreshCw className="w-6 h-6 text-gray-300 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-6">
      {/* CSS for animations */}
      <style>{`
        @keyframes carMove {
          0% { left: 0; }
          100% { left: calc(100% - 14px); }
        }
        .animate-car-move {
          animation: carMove 2s ease-in-out infinite alternate;
        }
        @keyframes slideIn {
          0% { transform: translateX(100%); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out forwards;
        }
      `}</style>

      <NewRideNotification
        isOpen={!!newOrderPopup}
        ride={newOrderPopup}
        onClose={() => setNewOrderPopup(null)}
        onAssignManual={(ride) => {
          setNewOrderPopup(null);
          navigate(`/admin/rides/${ride.id}`);
        }}
        queueHasDrivers={false}
      />

      {/* Header with Create Order Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LiveClock isConnected={isConnected} />
        </div>
        <div className="flex items-center gap-2">
          {/* Create Order Button */}
          <button 
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-white rounded-lg font-medium text-xs hover:opacity-90 transition-opacity shadow-sm"
            style={{ backgroundColor: '#f30001' }}
            data-testid="create-order-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Udhëtim i Ri</span>
          </button>
          <PeriodSelector value={period} onChange={setPeriod} customRange={customRange} onCustomRangeChange={setCustomRange} />
          <button onClick={() => { fetchDashboard(); fetchDrivers(); fetchRides(); }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <RefreshCw className={`w-4 h-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* KPI Cards - 5 cards, including cash */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KPICard 
          title="Udhëtime" 
          value={kpi.total_rides || 0} 
          subtitle={`${kpi.completed_rides || 0} përfunduar`} 
          icon={Clock} 
          color="text-blue-500"
          bgColor="bg-blue-50"
          onClick={() => navigate('/admin/rides')}
        />
        <KPICard 
          title="Aktive" 
          value={kpi.active_rides || 0} 
          subtitle={`${kpi.pending_rides || 0} në pritje`} 
          icon={Activity} 
          color="text-orange-500"
          bgColor="bg-orange-50"
          onClick={() => navigate('/admin/rides?status=active')}
        />
        <RevenueCard 
          amountAll={kpi.revenue_all} 
          amountEur={kpi.revenue_eur}
          onClick={() => navigate('/admin/finance')}
        />
        <KPICard 
          title="Cash te Shoferët" 
          value={formatLEK(unsettledCash.grand_total_all)} 
          subtitle={`${unsettledCash.drivers_count || 0} shoferë`} 
          icon={HandCoins} 
          color="text-amber-600"
          bgColor="bg-amber-50"
          onClick={() => navigate('/admin/cash-settlement')}
        />
        <KPICard 
          title="Shoferë" 
          value={`${kpi.online_drivers || 0}/${kpi.total_drivers || 0}`} 
          subtitle={`${kpi.busy_drivers || 0} në rrugë`} 
          icon={Users} 
          color="text-emerald-500"
          bgColor="bg-emerald-50"
          onClick={() => navigate('/admin/drivers')}
        />
      </div>

      {/* Map Section - Separate */}
      <div className="grid grid-cols-4 gap-4">
        {/* Map - 75% (3/4) */}
        <div className="col-span-3 bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800">Harta Live</h3>
            <button onClick={() => navigate('/admin/map')} className="text-xs text-blue-500 hover:underline">
              Harta e plotë →
            </button>
          </div>
          <div style={{ height: '380px' }}>
            <LiveDriversMap 
              drivers={drivers} 
              onDriverClick={(d) => navigate(`/admin/drivers/${d.id}`)} 
              pendingRides={kpi.pending_rides || 0}
            />
          </div>
        </div>
        
        {/* Live Orders - 25% (1/4) - Fixed height to show ~5 orders */}
        <div className="col-span-1 bg-white rounded-xl border border-gray-100 overflow-hidden" style={{ height: '428px' }}>
          <LiveOrdersPanel 
            rides={rides}
            onRideClick={(r) => navigate(`/admin/rides/${r.id}`)}
          />
        </div>
      </div>

      {/* Charts + Top Drivers */}
      <div className="grid lg:grid-cols-3 gap-4">
        <RevenueChart data={charts.daily} />
        <RidesChart data={charts.daily} />
        <TopDriversCard 
          drivers={drivers} 
          period={period}
          onClick={(d) => navigate(`/admin/drivers/${d.id}`)}
        />
      </div>

      {/* Create Order Modal */}
      <CreateOrderModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        drivers={drivers}
        onSuccess={() => {
          setShowCreateModal(false);
          fetchRides();
        }}
      />
    </div>
  );
};

export default AdminDashboardV2;
